/**
 * CoachFeedSection — curated next-best-action list shown at the top of
 * the bell dropdown. Replaces the "static event log" feel with a
 * proactive coach: streak-at-risk, cards-due, lecture-without-cards,
 * predictive-paywall, exam countdown, refresh-stale-subject.
 *
 * Items are sourced from /api/coach/feed (max 6, sorted by priority).
 * Dismissed items are stored in localStorage so the same nudge doesn't
 * keep coming back inside the same calendar day.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Flame, Zap, Sparkles, Lock, AlarmClock, RotateCcw, ArrowRight, X,
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'isp_coach_dismissed_v1';

const ICONS = {
  flame: Flame,
  lightning: Zap,
  sparkles: Sparkles,
  lock: Lock,
  alarm: AlarmClock,
  rotate: RotateCcw,
};

const PRIORITY_TINT = {
  1: { bg: 'bg-red-50',     border: 'border-red-200',     accent: 'text-red-600' },
  2: { bg: 'bg-orange-50',  border: 'border-orange-200',  accent: 'text-orange-600' },
  3: { bg: 'bg-amber-50',   border: 'border-amber-200',   accent: 'text-amber-700' },
  4: { bg: 'bg-blue-50',    border: 'border-blue-200',    accent: 'text-[#002FA7]' },
  5: { bg: 'bg-slate-50',   border: 'border-slate-200',   accent: 'text-slate-600' },
};

const dayKey = () => new Date().toISOString().slice(0, 10);
const readDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed?.day === dayKey() ? (parsed.ids || {}) : {};
  } catch { return {}; }
};
const writeDismissed = (ids) => {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ day: dayKey(), ids }));
  } catch { /* localStorage unavailable */ }
};

const CoachFeedSection = ({ onItemNavigate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(readDismissed);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/coach/feed`)
      .then(({ data }) => {
        if (cancelled) return;
        setItems(data?.items || []);
      })
      .catch(() => { /* feed is best-effort; silent on failure */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => items.filter((i) => !dismissed[i.id]), [items, dismissed]);
  if (loading || visible.length === 0) return null;

  const dismiss = (id) => {
    const next = { ...dismissed, [id]: true };
    setDismissed(next);
    writeDismissed(next);
  };

  return (
    <div className="border-b border-[var(--isp-glass-border)] bg-white" data-testid="coach-feed-section">
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <h4 className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#002FA7] flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Your next move
        </h4>
        <span className="text-[10px] text-[var(--isp-muted)]">{visible.length}</span>
      </div>
      <div className="px-3 pb-3 space-y-2">
        {visible.map((item) => {
          const Icon = ICONS[item.icon] || Sparkles;
          const tint = PRIORITY_TINT[item.priority] || PRIORITY_TINT[3];
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className={`group relative rounded-xl border ${tint.border} ${tint.bg} p-3 flex items-start gap-3 hover:shadow-sm transition-shadow`}
              data-testid={`coach-item-${item.kind}`}
            >
              <div className={`w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${tint.accent}`} aria-hidden />
              </div>
              <button
                type="button"
                onClick={() => {
                  if (item.url) {
                    navigate(item.url);
                    onItemNavigate?.();
                  }
                }}
                className="flex-1 min-w-0 text-left"
              >
                <p className="text-xs font-semibold text-[var(--isp-heading)] leading-tight">{item.title}</p>
                <p className="text-[11px] text-[var(--isp-body)] mt-0.5 line-clamp-2">{item.body}</p>
                <span className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${tint.accent}`}>
                  {item.cta} <ArrowRight className="w-3 h-3" />
                </span>
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); dismiss(item.id); }}
                className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full text-[var(--isp-muted)] hover:bg-white/80 hover:text-[var(--isp-heading)] flex items-center justify-center opacity-60 sm:opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Dismiss"
                data-testid={`coach-dismiss-${item.kind}`}
              >
                <X className="w-3 h-3" />
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default CoachFeedSection;
